import React, { useRef, FC } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { personalInfo } from '../utils/data';
import useSound from '../hooks/useSound';
import * as THREE from 'three';

// Define CSS for social links outside the component
const socialLinkStyles = `
  .social-link {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.7);
    border: 2px solid #00ff00;
    color: #00ff00;
    font-size: 24px;
    transition: all 0.3s;
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  }
  
  .social-link:hover {
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.8);
    background-color: rgba(0, 50, 0, 0.8);
  }
`;

interface SocialLink {
  name: string;
  url: string;
  icon: string;
}

interface SocialLinksProps {
  position?: [number, number, number];
  size?: number;
}

const SocialLinks: FC<SocialLinksProps> = ({ position = [0, 0, 0], size = 3 }) => {
  const groupRef = useRef<THREE.Group>(null);
  const { playClick, playHover } = useSound();
  
  // Social links from data
  const { github, linkedin, instagram, website } = personalInfo.social;
  
  const socialLinks: SocialLink[] = [
    { name: 'GitHub', url: github.url, icon: 'fab fa-github' },
    { name: 'LinkedIn', url: linkedin.url, icon: 'fab fa-linkedin' },
    { name: 'Instagram', url: instagram.url, icon: 'fab fa-instagram' },
    { name: 'Website', url: website.url, icon: 'fas fa-globe' },
  ];
  
  // Define element variables for JSX Three.js components
  const GroupElement = 'group' as any;
  const MeshElement = 'mesh' as any;
  const PlaneGeometryElement = 'planeGeometry' as any;
  const MeshBasicMaterialElement = 'meshBasicMaterial' as any;
  
  // Orbit animation
  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    
    const t = clock.getElapsedTime();
    const radius = size;
    
    // Position each icon in a circle around the specified position
    const iconElements = groupRef.current.children;
    const iconCount = iconElements.length;
    
    for (let i = 0; i < iconCount; i++) {
      const angle = (i / iconCount) * Math.PI * 2 + t * 0.2;
      
      const x = position[0] + Math.cos(angle) * radius;
      const y = position[1] + Math.sin(angle) * radius;
      const z = position[2];
      
      iconElements[i].position.set(x, y, z);
      
      // Always face the camera
      iconElements[i].rotation.x = 0;
      iconElements[i].rotation.y = 0;
      iconElements[i].rotation.z = 0;
    }
  });
  
  return (
    <GroupElement ref={groupRef}>
      {socialLinks.map((link, index) => (
        <MeshElement key={index} position={[0, 0, 0]}>
          <PlaneGeometryElement args={[1, 1]} />
          <MeshBasicMaterialElement 
            color="#000000"
            transparent
            opacity={0}
          />
          <Html center transform>
            <div>
              <a 
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                onMouseEnter={playHover}
                onClick={playClick}
                title={link.name}
              >
                <i className={link.icon}></i>
              </a>
              <style>{socialLinkStyles}</style>
            </div>
          </Html>
        </MeshElement>
      ))}
    </GroupElement>
  );
};

export default SocialLinks;